import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from '@app/core/i18n';

import { FieldBehaviorMode } from '@app/queryTyping';

import { CurrencyNumberFormat } from '@app/common/utils/currencyNumberFormat/currencyNumberFormat';
import { useCommonValidationMessages } from '@app/common/valiation/commonValidationMessages';

import { FieldComponent, FieldLocator } from '../types';
import { combineNames, getFieldErrorMessage, isFuzzyValueField } from '../utils';

import { FuzzyValueFieldFragment as FuzzyValueField } from '../queries/queryTyping/all-fields';
import { CFFormField } from './common/CFFormField';

/**
 * Represents the FuzzyValueFieldInfo configurable field.
 *
 * Operates the dollar amount value input:
 *  * value has range of min and max
 *  * character length limited
 *  * input text has currency symbol prefix
 *
 * Used for Last Deposit Amount field of User Enrollment.
 */
export const FuzzyValueFieldComponent: FieldComponent<FuzzyValueField> = ({
  parent,
  field,
}) => {
  const {
    formState: { errors },
    control,
  } = useFormContext();

  const { t } = useTranslation();
  const required = field.fieldBehaviorMode === FieldBehaviorMode.REQUIRED;
  const name = combineNames(parent, field.id);
  const commonValidationMessages = useCommonValidationMessages();
  const error = getFieldErrorMessage(name, errors, commonValidationMessages);

  // Range values are common across back-end and frontend
  const minValue = field.minValue || 0.0;
  const maxValue = field.maxValue || 9999999.99;

  // Text constants are currently equal, however we expect to change them soon
  const validationRules = {
    required,
    min: {
      value: minValue,
      message: t(
        'validation.minValue|Validation message for FuzzyValueField minimum value',
        'Please enter a valid dollar amount',
      ),
    },
    max: {
      value: maxValue,
      message: t(
        'validation.maxValue|Validation message for FuzzyValueField maximum value',
        'Please enter a valid dollar amount',
      ),
    },
  };

  return (
    <Controller
      name={name}
      defaultValue={field.nValue || ''}
      render={(renderProps) => (
        <CFFormField error={error}>
          <CurrencyNumberFormat
            aria-invalid={renderProps.fieldState.invalid}
            maxLength={14}
            allowNegative={false}
            label={field.label || ''}
            data-stable-name={`${field.id}Input`}
            {...renderProps.field}
          />
        </CFFormField>
      )}
      control={control}
      rules={validationRules}
    />
  );
};

export const FuzzyValueFieldLocator: FieldLocator = (context) => (
  isFuzzyValueField(context.field)
    ? FuzzyValueFieldComponent
    : undefined
);
